import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CheckCircle2,
  Clock3,
  MessageSquareText,
  Sparkles,
  Tags,
  TimerReset,
} from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import { useApi } from "@/hooks/use-api";
import { apiFetch, ApiError } from "@/lib/api-client";
import { formatCurrency, formatDateTime, formatLongWeekdayMonthDay, formatTime, formatWeekdayDay } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type PublicBookingMode = "simple_list" | "category_browse" | "intent_first";

interface PublicBookableServiceSummary {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  deliveryMode: string;
  locationMode: string;
  bookingFlow: string;
  categoryLabel: string;
  priceCents: number | null;
  currency: string;
  intakeRequired: "first_booking" | "every_booking" | "never";
  hasIntakeForm: boolean;
}

interface PublicBookingServicesPayload {
  services: PublicBookableServiceSummary[];
  recommendedMode: PublicBookingMode;
  availableModes: PublicBookingMode[];
}

interface PublicBookingIntentPayload {
  query: string;
  summary: string;
  source: "ai" | "fallback";
  matches: Array<{
    serviceId: string;
    serviceName: string;
    categoryLabel: string;
    reason: string;
    confidence: number;
  }>;
}

interface PublicAvailabilityServiceSummary {
  id: string;
  name: string;
  durationMinutes: number;
  deliveryMode: string;
  locationMode: string;
  bookingFlow: string;
}

interface PublicAvailabilitySlotPayload {
  start: string;
  end: string;
  available: true;
}

interface PublicAvailabilityRuntimeState {
  status: "ready" | "setup_required" | "action_required";
  code: string;
  setupRequired: boolean;
  canAcceptBookings: boolean;
  canSelfServeBook: boolean;
  message: string;
}

interface PublicAvailabilityPayload {
  date: string;
  timezone: string;
  service: PublicAvailabilityServiceSummary | null;
  slots: PublicAvailabilitySlotPayload[];
  runtime: PublicAvailabilityRuntimeState;
}

interface PublicBookingCreatePayload {
  data: {
    bookingId: string;
    personId: string;
    status: "confirmed";
  };
}

interface PublicIntakeFormField {
  id: string;
  type:
    | "text"
    | "email"
    | "phone"
    | "number"
    | "dropdown"
    | "checkbox"
    | "radio"
    | "textarea"
    | "date"
    | "file"
    | "heading"
    | "paragraph"
    | "divider";
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

interface PublicIntakeFormPayload {
  serviceId: string;
  intakeRequired: "first_booking" | "every_booking" | "never";
  requiresResponse: boolean;
  form: {
    id: string;
    name: string;
    mode: string;
    status?: string;
    fields: PublicIntakeFormField[];
  } | null;
}

interface PublicIntakeUploadPayload {
  data: {
    id: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
  };
}

interface PublicBookingRuntimeProps {
  tenantId: string | null;
  initialServiceId?: string | null;
}

interface BookingFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

interface IntakeUpload {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

const MODE_META: Record<PublicBookingMode, { label: string; description: string; Icon: typeof Sparkles }> = {
  intent_first: {
    label: "Intent-first",
    description: "Describe the outcome you want and let AI rank the closest live services.",
    Icon: Sparkles,
  },
  category_browse: {
    label: "Browse by category",
    description: "Move from service family to the exact service you want.",
    Icon: Tags,
  },
  simple_list: {
    label: "Simple list",
    description: "Choose from the full service list and book directly.",
    Icon: Calendar,
  },
};

const EMPTY_FORM: BookingFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  notes: "",
};

function formatDateParam(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getUpcomingDates(count = 10): Date[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: count }, (_, index) => {
    const next = new Date(today);
    next.setDate(today.getDate() + index);
    return next;
  });
}

function formatPrice(priceCents: number | null, currency: string): string {
  if (priceCents === null) {
    return "Pricing available on request";
  }

  return formatCurrency(priceCents, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatSlotLabel(isoString: string): string {
  return formatTime(isoString);
}

function formatDateLabel(date: Date): { short: string; long: string } {
  return {
    short: formatWeekdayDay(date),
    long: formatLongWeekdayMonthDay(date),
  };
}

export function PublicBookingRuntime({
  tenantId,
  initialServiceId,
}: PublicBookingRuntimeProps) {
  const [selectedMode, setSelectedMode] = useState<PublicBookingMode>("simple_list");
  const [intentQuery, setIntentQuery] = useState("");
  const deferredIntentQuery = useDeferredValue(intentQuery);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(initialServiceId ?? null);
  const upcomingDates = useMemo(() => getUpcomingDates(), []);
  const [selectedDate, setSelectedDate] = useState(() => formatDateParam(upcomingDates[0] ?? new Date()));
  const [selectedSlotStart, setSelectedSlotStart] = useState<string | null>(null);
  const [form, setForm] = useState<BookingFormState>(EMPTY_FORM);
  const [intakeAnswers, setIntakeAnswers] = useState<Record<string, unknown>>({});
  const [intakeUploads, setIntakeUploads] = useState<Record<string, IntakeUpload>>({});
  const [uploadingFieldId, setUploadingFieldId] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmedBooking, setConfirmedBooking] = useState<{
    bookingId: string;
    serviceName: string;
    start: string;
  } | null>(null);

  const servicesPath = tenantId
    ? `/api/public/bookings/services?tenant_id=${encodeURIComponent(tenantId)}`
    : "";

  const {
    data: servicesData,
    isLoading: isLoadingServices,
    error: servicesError,
  } = useApi<PublicBookingServicesPayload>(servicesPath, { enabled: tenantId !== null });

  const services = servicesData?.services ?? [];
  const intentPath = tenantId && selectedMode === "intent_first" && deferredIntentQuery.trim().length >= 2
    ? `/api/public/bookings/intent?tenant_id=${encodeURIComponent(tenantId)}&query=${encodeURIComponent(deferredIntentQuery.trim())}`
    : "";

  const {
    data: intentData,
    isLoading: isLoadingIntent,
    error: intentError,
  } = useApi<PublicBookingIntentPayload>(intentPath, {
    enabled: tenantId !== null && selectedMode === "intent_first" && deferredIntentQuery.trim().length >= 2,
  });

  useEffect(() => {
    if (!servicesData) {
      return;
    }

    setSelectedMode(servicesData.recommendedMode);
  }, [servicesData]);

  useEffect(() => {
    if (services.length === 0) {
      setSelectedServiceId(null);
      return;
    }

    const initialMatch = initialServiceId && services.some((service) => service.id === initialServiceId);
    if (initialMatch) {
      setSelectedServiceId(initialServiceId);
      return;
    }

    setSelectedServiceId((current) => {
      if (current && services.some((service) => service.id === current)) {
        return current;
      }
      return services[0]?.id ?? null;
    });
  }, [initialServiceId, services]);

  const categories = useMemo(
    () => [...new Set(services.map((service) => service.categoryLabel))],
    [services],
  );

  useEffect(() => {
    if (categories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory((current) => current && categories.includes(current) ? current : categories[0] ?? null);
  }, [categories]);

  const intentMatchIds = useMemo(
    () => intentData?.matches?.map((match) => match.serviceId) ?? [],
    [intentData],
  );

  const intentMatchMeta = useMemo(
    () => new Map((intentData?.matches ?? []).map((match) => [match.serviceId, match])),
    [intentData],
  );

  useEffect(() => {
    if (selectedMode !== "intent_first" || intentMatchIds.length === 0) {
      return;
    }

    setSelectedServiceId((current) => current && intentMatchIds.includes(current) ? current : intentMatchIds[0] ?? null);
  }, [intentMatchIds, selectedMode]);

  const visibleServices = useMemo(() => {
    if (selectedMode === "intent_first") {
      if (!deferredIntentQuery.trim()) {
        return services;
      }

      const serviceMap = new Map(services.map((service) => [service.id, service]));
      return intentMatchIds
        .map((serviceId) => serviceMap.get(serviceId) ?? null)
        .filter((service): service is PublicBookableServiceSummary => service !== null);
    }

    if (selectedMode === "category_browse" && selectedCategory) {
      return services.filter((service) => service.categoryLabel === selectedCategory);
    }

    return services;
  }, [deferredIntentQuery, intentMatchIds, selectedCategory, selectedMode, services]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [selectedServiceId, services],
  );

  const intakeFormPath = tenantId && selectedService?.hasIntakeForm
    ? `/api/public/bookings/intake-form?tenant_id=${encodeURIComponent(tenantId)}&service_id=${encodeURIComponent(selectedService.id)}${form.email ? `&email=${encodeURIComponent(form.email)}` : ""}`
    : "";

  const {
    data: intakeData,
    isLoading: isLoadingIntake,
    error: intakeError,
  } = useApi<PublicIntakeFormPayload>(intakeFormPath, {
    enabled: tenantId !== null && selectedService?.hasIntakeForm === true,
  });

  const availabilityPath = tenantId && selectedService
    ? `/api/public/availability?tenant_id=${encodeURIComponent(tenantId)}&service_id=${encodeURIComponent(selectedService.id)}&date=${encodeURIComponent(selectedDate)}`
    : "";

  const {
    data: availability,
    isLoading: isLoadingAvailability,
    error: availabilityError,
    refetch: refetchAvailability,
  } = useApi<PublicAvailabilityPayload>(availabilityPath, {
    enabled: tenantId !== null && selectedService !== null,
  });

  useEffect(() => {
    setSelectedSlotStart(null);
    setSubmitState("idle");
    setSubmitError(null);
    setConfirmedBooking(null);
    setIntakeAnswers({});
    setIntakeUploads({});
    setUploadingFieldId(null);
  }, [selectedDate, selectedServiceId]);

  const slots = availability?.slots ?? [];
  const selectedSlot = slots.find((slot) => slot.start === selectedSlotStart) ?? null;
  const selectedDateLabel = useMemo(() => {
    const date = upcomingDates.find((candidate) => formatDateParam(candidate) === selectedDate) ?? new Date(selectedDate);
    return formatDateLabel(date).long;
  }, [selectedDate, upcomingDates]);

  const activeIntakeForm = intakeData?.form ?? null;
  const requiresIntake = intakeData?.requiresResponse === true && activeIntakeForm !== null;

  function updateIntakeAnswer(fieldId: string, value: unknown) {
    setIntakeAnswers((current) => ({
      ...current,
      [fieldId]: value,
    }));
  }

  async function handleIntakeUpload(fieldId: string, file: File | null) {
    if (!file || !tenantId || !selectedService) {
      return;
    }

    setUploadingFieldId(fieldId);
    setSubmitError(null);

    try {
      const body = new FormData();
      body.set("tenant_id", tenantId);
      body.set("service_id", selectedService.id);
      body.set("field_id", fieldId);
      body.set("file", file);

      const response = await apiFetch<PublicIntakeUploadPayload>("/api/public/bookings/intake-upload", {
        method: "POST",
        body,
      });

      setIntakeUploads((current) => ({
        ...current,
        [fieldId]: response.data,
      }));
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : error instanceof Error
          ? error.message
          : "File upload failed. Please try again.";
      setSubmitError(message);
    } finally {
      setUploadingFieldId((current) => current === fieldId ? null : current);
    }
  }

  async function handleBookingSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!tenantId || !selectedService || !selectedSlot) {
      return;
    }

    setSubmitState("submitting");
    setSubmitError(null);

    try {
      if (requiresIntake && activeIntakeForm) {
        for (const field of activeIntakeForm.fields) {
          if (!field.required) {
            continue;
          }

          if (field.type === "file") {
            if (!intakeUploads[field.id]) {
              throw new Error(`Please upload a file for "${field.label}".`);
            }
            continue;
          }

          const value = intakeAnswers[field.id];
          const hasValue = Array.isArray(value)
            ? value.length > 0
            : typeof value === "string"
              ? value.trim().length > 0
              : value !== undefined && value !== null;
          if (!hasValue) {
            throw new Error(`Please complete "${field.label}".`);
          }
        }
      }

      const response = await apiFetch<PublicBookingCreatePayload>("/api/public/bookings", {
        method: "POST",
        body: JSON.stringify({
          tenant_id: tenantId,
          service_id: selectedService.id,
          slot_start: selectedSlot.start,
          slot_end: selectedSlot.end,
          customer: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phone: form.phone || undefined,
          },
          notes: form.notes || undefined,
          intake: requiresIntake && activeIntakeForm
            ? {
              answers: intakeAnswers,
              uploadIds: Object.values(intakeUploads).map((upload) => upload.id),
            }
            : undefined,
        }),
      });

      setSubmitState("success");
      setConfirmedBooking({
        bookingId: response.data.bookingId,
        serviceName: selectedService.name,
        start: selectedSlot.start,
      });
      setForm(EMPTY_FORM);
      setIntakeAnswers({});
      setIntakeUploads({});
      refetchAvailability();
    } catch (error) {
      const message = error instanceof ApiError
        ? error.message
        : error instanceof Error
          ? error.message
          : "Booking failed. Please try again.";
      setSubmitState("error");
      setSubmitError(message);
    }
  }

  if (!tenantId) {
    return (
      <div className="rounded-3xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/8 p-8">
        <div className="flex items-start gap-3">
          <TimerReset className="mt-1 h-5 w-5 text-[var(--color-warning)]" />
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Workspace context is missing</h2>
            <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
              The booking runtime needs an active Stero workspace so the public booking contracts know which tenant to serve.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
        <section className="overflow-hidden rounded-[28px] border border-[var(--color-border)] bg-[linear-gradient(145deg,var(--color-surface),color-mix(in_srgb,var(--color-surface)_78%,transparent))] shadow-[0_24px_80px_-48px_rgba(15,23,42,0.55)]">
          <div className="border-b border-[var(--color-border)] px-6 py-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Canonical booking runtime</p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-[var(--color-foreground)]">
                  Book the same way your public visitors do
                </h2>
                <p className="mt-2 text-sm leading-6 text-[var(--color-muted-foreground)]">
                  One Stero runtime for service discovery, live availability, and booking creation. No inspection-only dead end.
                </p>
              </div>
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
                <div className="font-medium text-[var(--color-foreground)]">{services.length || "—"} public services</div>
                <div className="mt-1">Recommended mode: {servicesData?.recommendedMode.replaceAll("_", " ") ?? "loading"}</div>
              </div>
            </div>
          </div>

          <div className="space-y-6 px-6 py-6">
            {isLoadingServices ? (
              <div className="space-y-4">
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-32 rounded-2xl" />
                <Skeleton className="h-20 rounded-2xl" />
              </div>
            ) : servicesError ? (
              <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-5 text-sm text-[var(--color-error)]">
                {servicesError}
              </div>
            ) : services.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
                <h3 className="text-lg font-semibold text-[var(--color-foreground)]">No public services are ready yet</h3>
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                  A service must be active, publicly listed, and open for self-booking before it appears in the public runtime.
                </p>
              </div>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-3">
                  {(servicesData?.availableModes ?? []).map((mode) => {
                    const meta = MODE_META[mode];
                    const Icon = meta.Icon;
                    const isActive = selectedMode === mode;

                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setSelectedMode(mode)}
                        className={cn(
                          "rounded-2xl border px-4 py-4 text-left transition-all",
                          isActive
                            ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-[0_12px_32px_-24px_color-mix(in_srgb,var(--color-primary)_55%,transparent)]"
                            : "border-[var(--color-border)] bg-[var(--color-background)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/4",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "rounded-xl p-2",
                            isActive ? "bg-[var(--color-primary)] text-white" : "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
                          )}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-medium text-[var(--color-foreground)]">{meta.label}</div>
                            <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">{meta.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {selectedMode === "intent_first" && (
                  <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
                    <label className="block text-sm font-medium text-[var(--color-foreground)]" htmlFor="intent-query">
                      What do you need help with?
                    </label>
                    <div className="relative mt-3">
                      <MessageSquareText className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                      <input
                        id="intent-query"
                        value={intentQuery}
                        onChange={(event) => setIntentQuery(event.target.value)}
                        placeholder="Example: I need a deep home clean before the weekend"
                        className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] pl-11 pr-4 text-sm text-[var(--color-foreground)] outline-none transition-colors placeholder:text-[var(--color-muted-foreground)] focus:border-[var(--color-primary)]"
                      />
                    </div>
                    <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                      Stero interprets the request, ranks the best public services, and then loads real slots for the selected match.
                    </p>
                    {intentQuery.trim().length >= 2 && (
                      <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-medium text-[var(--color-foreground)]">AI service matching</div>
                          <div className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                            {intentData?.source === "fallback" ? "Fallback" : "AI"}
                          </div>
                        </div>
                        <p className="mt-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
                          {isLoadingIntent
                            ? "Interpreting the request against the live public catalog..."
                            : intentError
                              ? intentError
                              : intentData?.summary ?? "Type a bit more context and Stero will rank the strongest matches."}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {selectedMode === "category_browse" && categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setSelectedCategory(category)}
                        className={cn(
                          "rounded-full border px-4 py-2 text-sm transition-colors",
                          selectedCategory === category
                            ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                            : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40",
                        )}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}

                <div className="grid gap-3">
                  {selectedMode === "intent_first" && intentQuery.trim() && !isLoadingIntent && visibleServices.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-6 text-sm text-[var(--color-muted-foreground)]">
                      No strong public service match yet. Refine the request with the outcome, service type, or location preference.
                    </div>
                  )}
                  {visibleServices.map((service) => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => setSelectedServiceId(service.id)}
                      className={cn(
                        "stagger-item rounded-3xl border px-5 py-5 text-left transition-all",
                        selectedServiceId === service.id
                          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-[0_18px_36px_-28px_color-mix(in_srgb,var(--color-primary)_55%,transparent)]"
                          : "border-[var(--color-border)] bg-[var(--color-background)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/4",
                      )}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="max-w-xl">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-semibold text-[var(--color-foreground)]">{service.name}</h3>
                            <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                              {service.categoryLabel}
                            </span>
                          </div>
                          <p className="mt-2 text-sm leading-6 text-[var(--color-muted-foreground)]">
                            {service.description ?? "Live availability will be shown as soon as you pick a date."}
                          </p>
                          {selectedMode === "intent_first" && intentMatchMeta.has(service.id) && (
                            <p className="mt-3 text-xs leading-5 text-[var(--color-primary)]">
                              {intentMatchMeta.get(service.id)?.reason}
                            </p>
                          )}
                        </div>
                        <div className="grid gap-2 text-sm text-[var(--color-muted-foreground)]">
                          <div className="flex items-center gap-2">
                            <Clock3 className="h-4 w-4 text-[var(--color-primary)]" />
                            {service.durationMinutes} min
                          </div>
                          <div>{formatPrice(service.priceCents, service.currency)}</div>
                          {selectedMode === "intent_first" && intentMatchMeta.has(service.id) && (
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-primary)]">
                              {Math.round((intentMatchMeta.get(service.id)?.confidence ?? 0) * 100)}% match
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] shadow-[0_22px_70px_-42px_rgba(15,23,42,0.45)]">
          <div className="border-b border-[var(--color-border)] px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Book now</p>
            <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">
              {selectedService?.name ?? "Select a service"}
            </h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              {selectedService
                ? `${selectedService.durationMinutes} minutes · ${formatPrice(selectedService.priceCents, selectedService.currency)}`
                : "Choose a public service from the left to load live availability."}
            </p>
          </div>

          <div className="space-y-6 px-6 py-6">
            {selectedService && (
              <>
                <div>
                  <p className="text-sm font-medium text-[var(--color-foreground)]">Choose a date</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {upcomingDates.map((date) => {
                      const label = formatDateLabel(date);
                      const value = formatDateParam(date);
                      const isActive = selectedDate === value;

                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setSelectedDate(value)}
                          className={cn(
                            "rounded-2xl border px-3 py-3 text-left transition-colors",
                            isActive
                              ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                              : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/35",
                          )}
                        >
                          <div className="text-xs uppercase tracking-[0.12em] opacity-80">{label.short}</div>
                          <div className="mt-1 text-sm font-medium">{label.long.split(", ")[0]}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                        Live availability
                      </h3>
                      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{selectedDateLabel}</p>
                    </div>
                    {availability?.timezone && (
                      <div className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
                        {availability.timezone}
                      </div>
                    )}
                  </div>

                  {isLoadingAvailability ? (
                    <div className="mt-4 space-y-3">
                      <Skeleton className="h-11 rounded-xl" />
                      <Skeleton className="h-11 rounded-xl" />
                      <Skeleton className="h-11 rounded-xl" />
                    </div>
                  ) : availabilityError ? (
                    <div className="mt-4 rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
                      {availabilityError}
                    </div>
                  ) : availability?.runtime.canSelfServeBook === false ? (
                    <div className="mt-4 rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/8 p-4 text-sm text-[var(--color-muted-foreground)]">
                      {availability.runtime.message}
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="mt-4 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-5 text-sm text-[var(--color-muted-foreground)]">
                      No live slots are open on this date yet. Pick another day or switch to another service.
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      {slots.map((slot) => (
                        <button
                          key={slot.start}
                          type="button"
                          onClick={() => setSelectedSlotStart(slot.start)}
                          className={cn(
                            "stagger-item rounded-2xl border px-4 py-3 text-left transition-all",
                            selectedSlotStart === slot.start
                              ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                              : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-primary)]/4",
                          )}
                        >
                          <div className="font-medium">{formatSlotLabel(slot.start)}</div>
                          <div className={cn(
                            "mt-1 text-xs",
                            selectedSlotStart === slot.start ? "text-white/80" : "text-[var(--color-muted-foreground)]",
                          )}>
                            {selectedService.durationMinutes} minutes
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedSlot && (
                  <form className="space-y-4" onSubmit={handleBookingSubmit}>
                    <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                            Your booking details
                          </h3>
                          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                            {selectedService.name} at {formatSlotLabel(selectedSlot.start)} on {selectedDateLabel}
                          </p>
                        </div>
                        <div className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
                          Secure booking
                        </div>
                      </div>

                      <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        {[
                          { key: "firstName", label: "First name", type: "text", required: true },
                          { key: "lastName", label: "Last name", type: "text", required: true },
                          { key: "email", label: "Email", type: "email", required: true },
                          { key: "phone", label: "Phone", type: "tel", required: false },
                        ].map((field) => (
                          <label key={field.key} className="block text-sm">
                            <span className="mb-2 block font-medium text-[var(--color-foreground)]">{field.label}</span>
                            <input
                              required={field.required}
                              type={field.type}
                              value={form[field.key as keyof BookingFormState]}
                              onChange={(event) => setForm((current) => ({
                                ...current,
                                [field.key]: event.target.value,
                              }))}
                              className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-[var(--color-foreground)] outline-none transition-colors focus:border-[var(--color-primary)]"
                            />
                          </label>
                        ))}
                      </div>

                      <label className="block text-sm">
                        <span className="mb-2 mt-4 block font-medium text-[var(--color-foreground)]">Notes for the team</span>
                        <textarea
                          value={form.notes}
                          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                          rows={4}
                          className="w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-[var(--color-foreground)] outline-none transition-colors focus:border-[var(--color-primary)]"
                          placeholder="Anything we should know before your booking?"
                        />
                      </label>
                    </div>

                    {selectedService.hasIntakeForm && (
                      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
                              Intake form
                            </h3>
                            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                              {isLoadingIntake
                                ? "Checking whether this booking needs intake questions..."
                                : requiresIntake
                                  ? activeIntakeForm?.name ?? "Complete the required intake questions before confirming."
                                  : intakeData?.intakeRequired === "first_booking"
                                    ? "This service only requires intake for first-time customers."
                                    : intakeData?.intakeRequired === "every_booking"
                                      ? "This service collects intake on every booking."
                                      : "No intake form is required for this booking."}
                            </p>
                          </div>
                          {requiresIntake && (
                            <div className="rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-medium text-[var(--color-primary)]">
                              Required
                            </div>
                          )}
                        </div>

                        {intakeError && (
                          <div className="mt-4 rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
                            {intakeError}
                          </div>
                        )}

                        {requiresIntake && activeIntakeForm && (
                          <div className="mt-5 grid gap-4">
                            {activeIntakeForm.fields.map((field: PublicIntakeFormField) => {
                              const value = intakeAnswers[field.id];
                              const uploadedFile = intakeUploads[field.id];

                              if (field.type === "heading") {
                                return (
                                  <h4 key={field.id} className="text-lg font-semibold text-[var(--color-foreground)]">
                                    {field.label}
                                  </h4>
                                );
                              }

                              if (field.type === "paragraph") {
                                return (
                                  <p key={field.id} className="text-sm leading-6 text-[var(--color-muted-foreground)]">
                                    {field.label}
                                  </p>
                                );
                              }

                              if (field.type === "divider") {
                                return <hr key={field.id} className="border-[var(--color-border)]" />;
                              }

                              return (
                                <label key={field.id} className="block text-sm">
                                  <span className="mb-2 block font-medium text-[var(--color-foreground)]">
                                    {field.label}
                                    {field.required && <span className="ml-1 text-[var(--color-error)]">*</span>}
                                  </span>

                                  {field.type === "textarea" ? (
                                    <textarea
                                      value={typeof value === "string" ? value : ""}
                                      onChange={(event) => updateIntakeAnswer(field.id, event.target.value)}
                                      rows={4}
                                      className="w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-[var(--color-foreground)] outline-none transition-colors focus:border-[var(--color-primary)]"
                                      placeholder={field.placeholder}
                                    />
                                  ) : field.type === "dropdown" ? (
                                    <select
                                      value={typeof value === "string" ? value : ""}
                                      onChange={(event) => updateIntakeAnswer(field.id, event.target.value)}
                                      className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-[var(--color-foreground)] outline-none transition-colors focus:border-[var(--color-primary)]"
                                    >
                                      <option value="">{field.placeholder ?? "Select..."}</option>
                                      {(field.options ?? []).map((option: string) => (
                                        <option key={option} value={option}>{option}</option>
                                      ))}
                                    </select>
                                  ) : field.type === "radio" ? (
                                    <div className="grid gap-2 sm:grid-cols-2">
                                      {(field.options ?? []).map((option: string) => (
                                        <button
                                          key={option}
                                          type="button"
                                          onClick={() => updateIntakeAnswer(field.id, option)}
                                          className={cn(
                                            "rounded-2xl border px-4 py-3 text-left transition-colors",
                                            value === option
                                              ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                                              : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]",
                                          )}
                                        >
                                          {option}
                                        </button>
                                      ))}
                                    </div>
                                  ) : field.type === "checkbox" ? (
                                    <div className="grid gap-2 sm:grid-cols-2">
                                      {(field.options ?? []).map((option: string) => {
                                        const selected = Array.isArray(value) && value.includes(option);
                                        return (
                                          <button
                                            key={option}
                                            type="button"
                                            onClick={() => {
                                              const current = Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
                                              updateIntakeAnswer(
                                                field.id,
                                                selected ? current.filter((entry) => entry !== option) : [...current, option],
                                              );
                                            }}
                                            className={cn(
                                              "rounded-2xl border px-4 py-3 text-left transition-colors",
                                              selected
                                                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                                                : "border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-foreground)]",
                                            )}
                                          >
                                            {option}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  ) : field.type === "file" ? (
                                    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-4">
                                      <input
                                        type="file"
                                        onChange={(event) => {
                                          const file = event.target.files?.[0] ?? null;
                                          void handleIntakeUpload(field.id, file);
                                        }}
                                        className="block w-full text-sm text-[var(--color-muted-foreground)]"
                                      />
                                      <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                                        Max 10 MB. Uploaded securely before booking confirmation.
                                      </p>
                                      {uploadingFieldId === field.id && (
                                        <p className="mt-2 text-xs font-medium text-[var(--color-primary)]">Uploading...</p>
                                      )}
                                      {uploadedFile && (
                                        <p className="mt-2 text-xs text-[var(--color-foreground)]">
                                          Attached: {uploadedFile.filename}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      type={
                                        field.type === "email"
                                          ? "email"
                                          : field.type === "phone"
                                            ? "tel"
                                            : field.type === "number"
                                              ? "number"
                                              : field.type === "date"
                                                ? "date"
                                                : "text"
                                      }
                                      value={typeof value === "string" ? value : ""}
                                      onChange={(event) => updateIntakeAnswer(field.id, event.target.value)}
                                      className="h-12 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 text-[var(--color-foreground)] outline-none transition-colors focus:border-[var(--color-primary)]"
                                      placeholder={field.placeholder}
                                    />
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {submitError && (
                      <div className="rounded-2xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/6 p-4 text-sm text-[var(--color-error)]">
                        {submitError}
                      </div>
                    )}

                    {submitState === "success" && confirmedBooking && (
                      <div className="rounded-3xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/8 p-5">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 text-[var(--color-success)]" />
                          <div>
                            <h3 className="font-semibold text-[var(--color-foreground)]">Booking confirmed</h3>
                            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                              {confirmedBooking.serviceName} is locked for {formatDateTime(confirmedBooking.start)}.
                            </p>
                            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                              Confirmation id: {confirmedBooking.bookingId}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitState === "submitting"}
                      className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[var(--color-primary)] px-5 text-sm font-semibold text-white transition-opacity hover:opacity-92 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitState === "submitting" ? "Confirming your booking..." : "Confirm booking"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
